import React, { useMemo, useState, useRef, useEffect, useId } from 'react';
import { Search } from 'lucide-react';
import { filterRecipients } from '../utils/recipientSearch';
import { getDisplayName } from '../utils/profile';

/**
 * Combobox com busca local para escolher um destinatário por `id` (uid).
 * @param {object} props
 * @param {string} props.id - id do input (label htmlFor)
 * @param {Array<{ id: string }>} props.options
 * @param {string} props.value - uid selecionado ou ''
 * @param {(uid: string, recipient: object|undefined) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {boolean} [props.required]
 * @param {boolean} [props.disabled]
 * @param {string} [props.ariaLabel]
 * @param {string} [props.ariaLabelledBy]
 */
export function RecipientCombobox({
  id,
  options = [],
  value,
  onChange,
  placeholder = 'Buscar colaborador...',
  required = false,
  disabled = false,
  ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) {
  const listboxId = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const selected = useMemo(() => options.find((o) => o.id === value), [options, value]);

  const filtered = useMemo(
    () => filterRecipients(options, open ? searchText : ''),
    [options, open, searchText]
  );

  const [highlightedIndex, setHighlightedIndex] = useState(0);

  useEffect(() => {
    setHighlightedIndex((i) => {
      if (filtered.length === 0) return 0;
      return Math.min(i, filtered.length - 1);
    });
  }, [filtered.length]);

  const displayValue = open ? searchText : selected ? getDisplayName(selected) : '';

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const pick = (recipient) => {
    onChange(recipient.id, recipient);
    setOpen(false);
    setSearchText(getDisplayName(recipient));
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault();
      setOpen(true);
      setSearchText(selected ? getDisplayName(selected) : '');
      setHighlightedIndex(0);
      return;
    }

    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => (filtered.length ? (i + 1) % filtered.length : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) =>
        filtered.length ? (i - 1 + filtered.length) % filtered.length : 0
      );
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const r = filtered[highlightedIndex];
      if (r) pick(r);
      return;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      {/* Validação nativa do form: só válido quando há uid selecionado (não basta digitar no campo visível). */}
      {required && (
        <input
          type="text"
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          required
          value={value ? '1' : ''}
          onChange={() => {}}
        />
      )}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
          aria-hidden
        />
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && filtered[highlightedIndex]
              ? `${id}-opt-${highlightedIndex}`
              : undefined
          }
          aria-required={required || undefined}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          autoComplete="off"
          disabled={disabled}
          placeholder={!selected && !open ? placeholder : undefined}
          value={displayValue}
          onChange={(e) => {
            setOpen(true);
            setSearchText(e.target.value);
            setHighlightedIndex(0);
          }}
          onFocus={() => {
            setOpen(true);
            setSearchText(selected ? getDisplayName(selected) : '');
            setHighlightedIndex(0);
          }}
          onKeyDown={handleKeyDown}
          className="w-full pl-10 pr-3 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none motion-safe:transition-[box-shadow] motion-safe:duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>

      {open && filtered.length > 0 && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-[60] mt-1 w-full max-h-48 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1 motion-safe:transition-opacity motion-safe:duration-150"
        >
          {filtered.map((r, idx) => {
            const label = getDisplayName(r);
            const active = idx === highlightedIndex;
            const isSelected = r.id === value;
            return (
              <li
                key={r.id}
                id={`${id}-opt-${idx}`}
                role="option"
                aria-selected={isSelected}
                className={`px-3 py-2.5 text-sm cursor-pointer motion-safe:transition-colors motion-safe:duration-150 ${
                  active ? 'bg-indigo-50 text-indigo-900' : 'text-slate-800 hover:bg-slate-50'
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onClick={() => pick(r)}
              >
                <span className="font-medium">{label}</span>
                {(r.email || r.username) && (
                  <span className="block text-xs text-slate-500 truncate">
                    {r.email || r.username}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {open && filtered.length === 0 && searchText.trim() !== '' && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-[60] mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg px-3 py-3 text-sm text-slate-500"
        >
          Nenhum colaborador encontrado.
        </div>
      )}
    </div>
  );
}
